import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './app/router';
import { api, ApiError } from './lib/api';
import { chaves } from './features/queries';
import './styles/globals.css';

/**
 * Política de cache e retentativa.
 *
 * O sistema atual não tem nem uma coisa nem outra: cada `fetch()` é solto, e
 * uma falha de rede momentânea vira modal de erro na cara do operador.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (tentativa, erro) => {
        // Não insiste no que não vai melhorar: 4xx é erro de quem chamou.
        if (erro instanceof ApiError && erro.status >= 400 && erro.status < 500) return false;
        return tentativa < 2;
      },
    },
    mutations: {
      // Escrita nunca é repetida automaticamente: repetir um POST de pagamento
      // registraria o dinheiro duas vezes.
      retry: false,
    },
  },
});

/**
 * A pergunta "quem é você?" sai ANTES do React montar.
 *
 * A Portaria não desenha tela nenhuma enquanto `/auth/me` não responde, e
 * ela só dispara essa consulta depois que o bundle avaliou, o React montou e
 * o roteador resolveu a rota. Em cima de um túnel de ~250 ms por requisição,
 * isso é espera em SÉRIE por nada: a resposta não depende de nada do React.
 *
 * Aqui a requisição parte no instante em que o módulo é avaliado e viaja em
 * paralelo com a montagem. Como a chave é a mesma que `useSessao` usa, o
 * `useQuery` da Portaria encontra a resposta já no cache (ou a promessa em
 * voo) e não pede de novo.
 *
 * A página do cliente (`/p/:token`) não tem sessão e ficaria com um 401 no
 * console à toa — por isso ela é a exceção, do mesmo jeito que é em
 * `useSessao(!publica)`.
 */
if (!location.pathname.startsWith('/p/')) {
  // `prefetchQuery` engole a falha por definição — quem não tem sessão cai no
  // `/login` pela Portaria, e não por uma exceção solta aqui.
  void queryClient.prefetchQuery({
    queryKey: chaves.sessao,
    queryFn: ({ signal }) => api.get('/auth/me', undefined, signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

const raiz = document.getElementById('root');
if (!raiz) throw new Error('Elemento #root não encontrado no index.html');

createRoot(raiz).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
